import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  useState,
  useCallback,
  startTransition,
  memo,
  useMemo,
  type ReactNode,
} from 'react'
import { cn, type FilterOption } from '@/lib/utils'
import { ListFilter } from 'lucide-react'

interface FiltersComboboxProps {
  /** List of available filters */
  filtersList?: FilterOption[]
  /** Trigger button text */
  triggerTitle?: string
  /** Search input placeholder */
  searchPlaceholder?: string
  /** Message when no filters found */
  emptyMessage?: string
  /** Icon to display before title */
  triggerIcon?: ReactNode
  /** Icon to display after title */
  triggerIconAfter?: ReactNode
  /** Additional class names */
  className?: string
  /** Disabled state */
  disabled?: boolean
  /** Currently selected filter keys */
  selectedFilters?: string[]
  /** Filter group number for OR logic */
  filtersGroup?: number
  /** Content alignment */
  contentAlign?: 'start' | 'center' | 'end'
  /** Callback when filter is selected */
  onFilterSelect?: (filterKey: string, filtersGroup: number) => void
  /** Whether filters/sorts are already applied (changes button to toggle) */
  hasAppliedFilters?: boolean
  /** Current visibility state of filters menu */
  isFiltersMenuVisible?: boolean
  /** Callback to toggle filters menu visibility */
  onFiltersMenuVisibleChange?: (visible: boolean) => void
}

function FiltersCombobox({
  filtersList = [],
  triggerTitle = 'Filters',
  searchPlaceholder = 'Filter By',
  emptyMessage = 'No filters found.',
  triggerIcon,
  triggerIconAfter,
  className,
  disabled = false,
  selectedFilters = [],
  filtersGroup = 0,
  contentAlign = 'end',
  onFilterSelect,
  hasAppliedFilters = false,
  isFiltersMenuVisible = true,
  onFiltersMenuVisibleChange,
}: FiltersComboboxProps) {
  const [open, setOpen] = useState(false)

  const sortedFiltersList = useMemo(() => {
    return [...filtersList]
      .filter((filter) => !selectedFilters.includes(filter.key))
      .sort((a, b) => {
        // Primary sort: visible items first
        if (a.isVisible && !b.isVisible) return -1
        if (!a.isVisible && b.isVisible) return 1
        // Secondary sort: alphabetical by label
        return a.label.localeCompare(b.label)
      })
  }, [filtersList, selectedFilters])

  const handleOnSelect = useCallback(
    (currentValue: string) => {
      onFilterSelect?.(currentValue, filtersGroup)
      startTransition(() => {
        setOpen(false)
      })
    },
    [onFilterSelect, filtersGroup]
  )

  const hasActiveFilters = selectedFilters.length > 0

  // When filters/sorts are already applied, show as toggle button
  if (hasAppliedFilters) {
    return (
      <Button
        disabled={disabled}
        variant="outline"
        onClick={() => onFiltersMenuVisibleChange?.(!isFiltersMenuVisible)}
        className={cn(
          'relative flex-1 gap-1 pl-2 pr-3 font-univers text-primary shadow-none [&>svg]:text-primary md:flex-initial',
          hasActiveFilters &&
            'border-primary bg-primary text-white [&>svg]:text-white hover:bg-primary/90 hover:text-white hover:[&>svg]:text-white',
          className
        )}
      >
        {triggerIcon ?? <ListFilter className="h-3.5 w-3.5" />}
        {triggerTitle}
        {triggerIconAfter}
        {hasActiveFilters && (
          <span className="flex items-center justify-center size-4 ml-0.5 rounded-full bg-white text-[10px] font-inter text-primary">
            {selectedFilters.length}
          </span>
        )}
      </Button>
    )
  }

  // Otherwise, show as popover combobox for adding new filters
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          disabled={disabled}
          variant="outline"
          role="combobox"
          aria-label={triggerTitle}
          aria-expanded={open}
          className={cn(
            'relative flex-1 gap-1 pl-2 pr-3 font-univers text-primary shadow-none [&>svg]:text-primary md:flex-initial',
            'data-[state=open]:border-primary data-[state=open]:bg-primary data-[state=open]:text-white data-[state=open]:[&>svg]:text-white',
            hasActiveFilters &&
              'border-primary bg-primary text-white [&>svg]:text-white hover:bg-primary/90 hover:text-white hover:[&>svg]:text-white',
            className
          )}
        >
          {triggerIcon ?? <ListFilter className="h-3.5 w-3.5" />}
          {triggerTitle}
          {triggerIconAfter}
          {hasActiveFilters && (
            <span className="flex items-center justify-center size-4 ml-0.5 rounded-full bg-white text-[10px] font-inter text-primary">
              {selectedFilters.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[248px] p-0 border-sidebar-border"
        align={contentAlign}
        sideOffset={8}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {sortedFiltersList.map((filter) => (
                <CommandItem
                  key={filter.key}
                  value={filter.label}
                  onSelect={() => handleOnSelect(filter.key)}
                  className="cursor-pointer"
                >
                  {filter.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const MemoizedFiltersCombobox = memo(FiltersCombobox)
MemoizedFiltersCombobox.displayName = 'FiltersCombobox'

export { MemoizedFiltersCombobox as FiltersCombobox }
